import {SagaIterator} from "redux-saga";
import {call, Effect, fork, put, select, take, takeEvery,} from "redux-saga/effects";
import {LOCATION_CHANGE, LocationChangeAction, push} from "connected-react-router";
import {ICategoryProductFilterPage, IPage, IProduct, IProductFilterPage} from "@montana/models/generated/cms.interface";
import {fetchPageData} from "@montana/services/content";
import {StatusCodes} from "@montana/constants/status-codes";
import {scrollTop} from "@montana/helpers/scroll.helper";
import {IStoreState} from "@montana/store/store";
import {getPath, getUrlParams} from "@montana/helpers/location.helper";
import {
    convertMarketUrlToLanguageUrl,
    getLanguageFromUrl,
    marketRegex,
    transformUrlToLanguageFallback,
} from "@montana/helpers/market.helper";
import {fetchPageData as announceFetchPageData, fetchPageDataError, fetchPageDataSuccess} from "./page.actions";
import {AppActions} from "../app/app.actions";
import {error} from "@montana/services/logger";
import {isBrowser, isServer} from "@montana/helpers/browser.helper";
import {IPageContainerState, IPageError} from "./page";
import {logout} from "../authentication/authentication.actions";
import {
    CategoryProductFilterPageKind,
    ConfirmationPageKind,
    ErrorPageKind,
    LoadingPageKind,
    ProductFilterPageKind,
    StoreLocatorPageKind
} from "@montana/pages/dynamic-page.component";
import {setSessionStorageItem} from "@montana/helpers/sessionstorage.helper";
import {REDIRECT_ON_LOGIN} from "@montana/constants/sessionstorage";
import {fetchProductInfo} from "@montana/services/product";
import {getProductVariant, onProductInit} from "../product/product.sagas";
import {onLoadOrderInformation} from "../checkout/checkout.sagas";
import {onInitFilter} from "../filter/filter.sagas";
import {onGetStores} from "../stores/stores.sagas";
import {connectServiceFunctions, selectCurrentUrlLocale} from "../base/base.sagas";
import {OmitConfig} from "@montana/models/dev";
import {IDynamicSettingsContainerState} from "../dynamic-settings/dynamic-settings";
import {ISettingsContainerState} from "../settings/settings";
import {DEFAULT_LANGUAGE} from "@montana/constants/defaults";

let lastPath = '';

export function* getPageContent(path: string, initialLoad?: boolean): SagaIterator {
    let errorResult: IPageError | undefined;

    try {
        yield put(announceFetchPageData());
        const { links, markets }: IDynamicSettingsContainerState & ISettingsContainerState = yield select((s: IStoreState) => ({ ...s.settings, ...s.dynamicSettings }));
        const { customerMode, language }: ISettingsContainerState = yield select((s: IStoreState) => s.settings);
        const currentUrlLocale: string = yield selectCurrentUrlLocale(language);


        let pagePath = path;

        if (markets) {
            const context = getLanguageFromUrl(pagePath);
            if (context && marketRegex.test(context)) {
                pagePath = convertMarketUrlToLanguageUrl(pagePath, markets);
            }
        }

        const productId = getProductId(path);

        const rawProductPageUrl = convertMarketUrlToLanguageUrl((customerMode === 'professional' ? links?.contractorProductPage : links?.productPage) || '', markets);
        const productPageUrl = rawProductPageUrl ? transformUrlToLanguageFallback(rawProductPageUrl, currentUrlLocale) : '';

        if (productId) {
            // Set page
            const loadingPage: IPageContainerState = {
                kind: LoadingPageKind,
                contentType: 'string',
                epiId: 0,
            };
            yield put(fetchPageDataSuccess(loadingPage));
        }

        // todo: Remember to remove this when CMS data is available
        /*if (path.includes('test-mock-page')) {
            return yield put(fetchPageDataSuccess({...colourPageMockResponse}))
        }*/

        const [cFetchPageData]: [OmitConfig<typeof fetchPageData>] = yield connectServiceFunctions(fetchPageData);
        const response: Response = (yield call(cFetchPageData, productId ? productPageUrl : pagePath, productId, customerMode, language)) as unknown as Response;

        const {
            status, json, ok, headers
        } = response;

        switch (status) {
            case StatusCodes.Redirect:
            case StatusCodes.TemporaryRedirect: {
                const pageData: IPageContainerState = {
                    kind: ErrorPageKind,
                    id: "id", // Necessary for page-transition to show page.
                    contentType: "string", // not optional, that's why...
                    epiId: 0,
                    redirect: headers.get("LocationAlternative") ?? undefined //Using custom location header. Normal location header automatically includes domain of BFF. We only want the relative URI.
                }
                return yield put(fetchPageDataSuccess(pageData));
            }
            case StatusCodes.Unauthorized: {
                if (!isBrowser) {
                    return yield put(fetchPageDataError({ error: new Error('unauthorized'), errorResponseCode: 401 }));
                }
                // The user was unautorize to access a page - redirect to login page.
                if (links?.login && links?.login !== path) {
                    if (path && path !== "/" && !path.includes("/login")) {
                        setSessionStorageItem(REDIRECT_ON_LOGIN, path);
                    }
                    if (initialLoad) {
                        // If load is initial, we dont need to logout, but just a push to login.
                        return yield put(push(links.login));
                    } else {
                        return yield put(logout());
                    }
                } else {
                    const errorPage: IPage = {
                        kind: ErrorPageKind,
                        id: "id", // Necessary for page-transition to show page.
                        contentType: "string", // not optional, that's why...
                        epiId: 0,

                    };
                    return yield put(fetchPageDataSuccess(errorPage));
                }
            }
            case StatusCodes.NotFound: {
                // It may seem weird to fire a "success"-action here, but doing anything else MIGHT mess up
                // the dynamic routing with regards to history-jumping. We'll pretend that it's a success here,
                // but the data that will be stored will have all the hallmarks of a proper error page – most
                // importantly the 404 status code.
                const pageData: IPage = yield call(json.bind(response));
                yield put(fetchPageDataSuccess(pageData));
                return yield put(fetchPageDataError({ errorResponseCode: 404 }));
            }
            default: {
                // Only allow processing if the reply was actually OK.
                if (ok) {
                    const pageData: IPageContainerState = yield call(json.bind(response));

                    // Transform HrefLang Urls
                    if (pageData.hrefLangs?.length) {
                        pageData.hrefLangs = pageData.hrefLangs.map(hrefLang => ({
                            languageCode: hrefLang?.languageCode,
                            url: hrefLang?.url,
                        }));
                    }

                    // Order Confirmation Page
                    const orderId = getUrlParams(path).find(p => p.key.toLowerCase() === 'orderid')?.value;

                    if (orderId && pageData.kind === ConfirmationPageKind) {
                        yield call(onLoadOrderInformation, orderId);
                    }

                    // Product Page
                    if (productId) {
                        const [cFetchProductInfo]: [OmitConfig<typeof fetchProductInfo>] = yield connectServiceFunctions(fetchProductInfo);
                        const productResponse: Response = yield call(cFetchProductInfo, productId, currentUrlLocale || DEFAULT_LANGUAGE, customerMode);

                        if (!productResponse.ok) {
                            throw new Error();
                        }

                        const product: IProduct | null = yield call(productResponse.json.bind(productResponse));

                        if (product) {
                            // Handle redirect if url is not matching canonical url and currently if it is not under professional node as these currently share canonical from BE.
                            if (isServer && pageData.canonicalUrl && !path.includes(encodeURI(pageData.canonicalUrl)) && !path.includes(links?.contractorFrontPage || 'no-match-please')) {
                                const pathQueryParams = path.split('?');

                                // Requested url contains query params and we need to transfer them to the canonical url
                                if (pathQueryParams.length > 1) {
                                    pageData.redirect = `${pageData.canonicalUrl.split('?')[0]}?${pathQueryParams[1]}`;
                                } else {
                                    pageData.redirect = pageData.canonicalUrl;
                                }

                                pageData.redirect = pageData.redirect.toLowerCase();

                                return yield put(fetchPageDataSuccess(pageData));
                            }

                            pageData.name = product.productTitleH1;

                            yield call(onProductInit, product, path);
                            yield call(getProductVariant);
                            /** @deprecated: Should be refactored to use London Dynamics AR implementation.;
                            ** yield call(onDoARFileExist);
                            */
                        } else {
                            yield put(fetchPageDataError({
                                error: new Error('Product wasn\'t found'),
                                errorResponseCode: 404
                            }));
                            return yield put(push(links?.homePage || '/'));
                        }
                    }

                    // Product List Page
                    if (pageData.kind === ProductFilterPageKind || pageData.kind === CategoryProductFilterPageKind) {
                        const filterPageData = pageData as IProductFilterPage | ICategoryProductFilterPage;
                        yield call(onInitFilter, filterPageData.filterId, filterPageData.filterLanguage || '', path, customerMode);
                    }

                    // Store Locator Page
                    if (pageData.kind === StoreLocatorPageKind && !!markets) {
                        yield call(onGetStores, markets);
                    }

                    return yield put(fetchPageDataSuccess(pageData));
                }
            }
        }
    } catch (e: any) {
        error(`Could not get page data for '${path}'. Error was: ${e.message}`);
        errorResult = { error: e, errorResponseCode: 500 };
    }


    if (!errorResult) {
        errorResult = { error: new Error("Unknown error"), errorResponseCode: 500 };
    }

    return yield put(fetchPageDataError(errorResult));
}

function getProductId(path: string): string | undefined {
    const params = getUrlParams(path);
    return params.find(p => p.key.toLowerCase() === 'product')?.value;
}

function* onLocationChange(action: LocationChangeAction): SagaIterator {
    const newPath = getPath(action.payload.location);

    if (newPath === lastPath || action.payload.action === "REPLACE") {
        return;
    }

    lastPath = newPath;
    scrollTop();

    yield call(getPageContent, newPath);
}

function* init(): IterableIterator<Effect> {
    lastPath = (yield select(({ router }: IStoreState): string => getPath(router.location))) as unknown as string;
}

function* watchLocationChange(): IterableIterator<IterableIterator<Effect> | Effect> {
    yield take(AppActions.INIT);
    yield init();
    yield takeEvery(LOCATION_CHANGE, onLocationChange);
}

export const pageSagas = [
    fork(watchLocationChange),
];
